import { API } from '@/api';
import { State } from '@/reducers/types';
import { MainReportType, ReportType, SaveReportData, UpdateReportData } from '@/types';
import { createAsyncThunk } from '@reduxjs/toolkit';

export const retrieveDocData = createAsyncThunk(
  'chat/docData',
  async ({ name, document_template_id }: { name: string; document_template_id: number }) =>
    API.chats.createDoc({ name, document_template_id }),
);

export const sendPromptsInputs = createAsyncThunk(
  'chat/sendInputs',
  async ({ inputs }: { inputs: any }) => API.chats.saveInputs({ inputs }),
);

export const buildDocument = createAsyncThunk(
  'chat/buildDoc',
  async ({ documentId }: { documentId: number }) => API.chats.buildDoc({ documentId }),
);

export const retrieveDocument = createAsyncThunk(
  'chat/getDoc',
  async ({
    documentId,
    type,
    option,
    ignorePostprocess,
  }: {
    documentId: number;
    type?: string;
    option?: string;
    ignorePostprocess?: boolean;
  }) => API.chats.getDocByID({ documentId, type, option, ignorePostprocess }),
);

export const createArit = createAsyncThunk(
  'chat/createArit',
  async ({ taaw, text }: { taaw: string; text: string }) =>
    API.byRelevance.createArit({ taaw, text }),
);

export const getIms = createAsyncThunk(
  'chat/getIms',
  async ({ url, promptInputs }: { url: string; promptInputs: any }) =>
    API.byRelevance.getImsResults(url, promptInputs),
);

export const createIms = createAsyncThunk(
  'chat/createIms',
  async ({
    obj,
    spo,
    region,
    ta,
    language,
  }: {
    obj: string;
    spo: string;
    region: string;
    ta: string;
    language: string;
  }) => API.byRelevance.createIms({ obj, spo, region, ta, language }),
);

export const getReportsList = createAsyncThunk(
  'reports/list',
  async ({
    lastReportId,
    limit,
    title,
    types,
  }: {
    lastReportId?: string;
    limit?: number;
    title?: string;
    types?: ReportType[];
  } = {}) => {
    return await API.reports.list({
      ...(lastReportId ? { lastReportId } : {}),
      ...(limit ? { limit } : {}),
      ...(title ? { title } : {}),
      ...(types && types.length > 0 ? { types: types.join(',') } : {}),
    });
  },
);

export const searchReports = createAsyncThunk('reports/search/get', async (value: string) => {
  return await API.reports.list({ search: value });
});

export const getReport = createAsyncThunk(
  'reports/get',
  async ({ id, type }: { id: string; type?: string }) => await API.reports.get({ id, type }),
);

export const getReportsByFlow = createAsyncThunk(
  'reports/getByFlow',
  async ({ flowId, types }: { flowId: string; types: ReportType[] }) =>
    await API.reports.getByFlow({ flowId, types }),
);

export const removeReport = createAsyncThunk(
  'reports/remove',
  async (
    { flowId, type, isSingle }: { flowId: string; type: string; isSingle: boolean },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      await API.reports.remove({ flowId, type, isSingle });
      const { filterValue } = (getState() as State).chats;
      dispatch(getReportsList({ types: filterValue }));
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateReportTitle = createAsyncThunk(
  'reports/updateTitle',
  async ({
    title,
    flowId,
    id,
    type,
  }: {
    title: string;
    flowId: string | null;
    id: string | null;
    type: MainReportType | null;
  }) => API.reports.updateTitle({ title, flowId, id, type }),
);

export const updateReport = createAsyncThunk(
  'reports/update',
  async ({ reportData }: { reportData: UpdateReportData }) => API.reports.update({ reportData }),
);

export const saveReport = createAsyncThunk(
  'reports/save',
  async ({ reportData }: { reportData: SaveReportData }) => API.reports.create({ reportData }),
);

export const getReportTypes = createAsyncThunk(
  'reports/types/get',
  async ({ userId }: { userId: string }) => await API.reports.getReportTypes({ userId }),
);

export const getUniqueTitle = createAsyncThunk(
  'reports/uniqueTitle',
  async ({ title }: { title: string }) => await API.reports.getUniqueTitle({ title }),
);

export const executeSimStudioWorkflow = createAsyncThunk(
  'simstudio/executeWorkflow',
  async ({
    promptText,
    type,
    documentId,
  }: {
    promptText: string;
    type:
      | 'pawGen'
      | 'pawSingleRegen'
      | 'conopPhase1Gen'
      | 'iraGen'
      | 'iraItemRegen'
      | 'ppagGen'
      | 'ppagRegen'
      | 'conopFinalGen'
      | 'igGen';
    documentId: string;
  }) =>
    API.bySimstudio.executeSimStudioWorkflow({
      promptText,
      type,
      documentId,
    }),
);
